import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, X, Check, Loader2 } from 'lucide-react';
import { http } from '../lib/api.js';
import { formatMoney } from '../lib/format.js';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * Who is buying.
 *
 * A counter needs two things a form cannot do: find somebody who has been in
 * before, and get out of the way for somebody who has not. So this searches as
 * the cashier types a phone number, shows the last few customers when the box is
 * empty, and stays entirely optional — a walk-in paying cash and leaving is the
 * common case, and holding them at the till for a name is how a queue forms.
 */
export function CustomerPicker({ value, onChange, accent = 'rgb(var(--ink))' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  /** The most recent handful, so an empty box is still useful. */
  useEffect(() => {
    let cancelled = false;
    http.get('/dashboard/customers', { params: { limit: 6 } })
      .then((d) => { if (!cancelled) setRecent(d.customers ?? []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Debounced, because a cashier types a ten-digit number faster than a round
  // trip completes and every keystroke would otherwise be a query.
  useEffect(() => {
    const term = query.trim();
    if (!term) { setResults([]); setSearching(false); return undefined; }

    setSearching(true);
    const timer = setTimeout(() => {
      http.get('/dashboard/customers', { params: { search: term, limit: 6 } })
        .then((d) => setResults(d.customers ?? []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // A tap outside puts the list away without clearing what was typed.
  useEffect(() => {
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const choose = (customer) => {
    onChange({ phone: customer.phone ?? '', name: customer.name ?? '', id: customer.id });
    setQuery('');
    setOpen(false);
  };

  const shown = query.trim() ? results : recent;

  /* ---------------------------- already chosen ---------------------------- */
  if (value?.id) {
    return (
      <div
        className="flex items-center gap-3 rounded-xl border px-3.5 py-3"
        style={{ borderColor: accent, background: 'rgb(var(--surface-2))' }}
      >
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold text-white"
          style={{ background: accent }}
          aria-hidden="true"
        >
          {(value.name || value.phone || '?').charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{value.name || value.phone}</span>
          {value.name && value.phone && (
            <span className="block truncate text-xs text-muted">{value.phone}</span>
          )}
        </span>
        <button
          type="button"
          onClick={() => onChange({ phone: '', name: '', id: null })}
          className="shrink-0 rounded-md p-1.5 text-faint transition-colors hover:bg-surface-3 hover:text-ink"
          aria-label="Clear customer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  /* ------------------------------- searching ------------------------------ */
  return (
    <div ref={box} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden="true" />
        <input
          type="search"
          className="input pl-9"
          placeholder="Search by phone or name, or leave blank"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          aria-label="Find a customer"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-faint" aria-hidden="true" />
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.14, ease: EASE }}
            className="absolute z-20 mt-1.5 max-h-72 w-full overflow-auto rounded-xl border bg-surface shadow-lg"
            style={{ borderColor: 'rgb(var(--line))' }}
          >
            {shown.length > 0 && (
              <p className="eyebrow border-b px-3.5 py-2" style={{ borderColor: 'rgb(var(--line-soft))' }}>
                {query.trim() ? 'Matches' : 'Recent customers'}
              </p>
            )}

            {shown.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => choose(c)}
                className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-surface-2"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-line bg-surface-2 text-xs font-semibold text-ink-soft">
                  {(c.name || c.phone || '?').charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">{c.name || c.phone}</span>
                  <span className="block truncate text-xs text-muted">
                    {c.name ? c.phone : c.email}
                    {c.succeededCount > 0 && ` · ${c.succeededCount} paid`}
                  </span>
                </span>
                {c.totalSpend > 0 && c.currency && (
                  <span className="tabular shrink-0 text-xs text-muted">
                    {formatMoney(c.totalSpend, c.currency)}
                  </span>
                )}
              </button>
            ))}

            {/* Typing a number nobody matches is the normal way a new customer
                is created — so offer it rather than showing "no results". */}
            {query.trim() && !searching && (
              <button
                type="button"
                onClick={() => { onChange({ phone: query.trim(), name: '', id: null }); setQuery(''); setOpen(false); }}
                className="flex w-full items-center gap-3 border-t px-3.5 py-2.5 text-left transition-colors hover:bg-surface-2"
                style={{ borderColor: 'rgb(var(--line-soft))' }}
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-dashed border-line text-faint">
                  <User className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm text-ink-soft">
                  Use <span className="font-medium text-ink">{query.trim()}</span> as a new customer
                </span>
              </button>
            )}

            {shown.length === 0 && !query.trim() && (
              <p className="px-3.5 py-4 text-center text-xs text-muted">
                No customers yet. Type a phone number to start one.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* A name for somebody the till has never seen. Only shown once a number
          has been entered, so it never gets in the way of an anonymous sale. */}
      {value?.phone && !value.id && (
        <div className="mt-2.5 flex items-center gap-2">
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
          <span className="shrink-0 text-xs text-muted">{value.phone}</span>
          <input
            type="text"
            className="input h-9 flex-1 text-sm"
            placeholder="Name (optional)"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            aria-label="Customer name"
          />
          <button
            type="button"
            onClick={() => onChange({ phone: '', name: '', id: null })}
            className="shrink-0 rounded-md p-1.5 text-faint transition-colors hover:bg-surface-2 hover:text-ink"
            aria-label="Clear customer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default CustomerPicker;
